import { useId, useRef, useState } from 'react';
import { api } from '../../../api/client.js';
import { ImageUploadField } from '../../../components/forms/ImageUploadField.jsx';
import { AttributesEditor } from './AttributesEditor.jsx';
import { OptionsEditor } from './OptionsEditor.jsx';
import { getCurrencySymbol } from '../../../utils/currency.js';
import { Modal } from '../../../components/Modal.jsx';
import { ConfirmDialog } from '../../../components/ConfirmDialog.jsx';

function Icon({ name, size = 19 }) {
  const paths = {
    close: <path d="M6 6l12 12M18 6 6 18" />,
    plate: <><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="4" /></>,
    image: <><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="10" r="2" /><path d="m21 15-4-4L6 20" /></>,
    sliders: <><path d="M4 7h10M18 7h2M4 17h2M10 17h10" /><circle cx="16" cy="7" r="2" /><circle cx="8" cy="17" r="2" /></>,
    info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></>,
    check: <path d="m5 12 4 4L19 6" />,
  };
  return <svg className="ui-icon" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
}

export function ItemFormModal({ menuId, categoryId, categoryName, currency, item, onClose, onSaved }) {
  const isEditing = Boolean(item);
  const detailsFormId = useId();
  const base = `/api/menus/${menuId}/categories/${categoryId}/items`;
  const [name, setName] = useState(item?.name ?? '');
  const [price, setPrice] = useState(item?.price == null ? '' : String(item.price));
  const [description, setDescription] = useState(item?.description ?? '');
  const [image, setImage] = useState(item?.imageUrl ? { url: item.imageUrl, id: item.imageMediaId } : null);
  const [isVisible, setIsVisible] = useState(item ? !item.hidden : true);
  const [isFeatured, setIsFeatured] = useState(item?.isFeatured ?? false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const initialValuesRef = useRef(JSON.stringify({
    name: item?.name ?? '',
    price: item?.price == null ? '' : String(item.price),
    description: item?.description ?? '',
    imageMediaId: item?.imageMediaId ?? null,
    isVisible: item ? !item.hidden : true,
    isFeatured: item?.isFeatured ?? false,
  }));

  function currentValues(override = {}) {
    return {
      name,
      price,
      description,
      imageMediaId: image?.id ?? null,
      isVisible,
      isFeatured,
      ...override,
    };
  }

  function change(field, setter, value) {
    setter(value);
    const normalizedValue = field === 'imageMediaId' ? value?.id ?? null : value;
    setDirty(JSON.stringify(currentValues({ [field]: normalizedValue })) !== initialValuesRef.current);
    setSaved(false);
    setError(null);
  }

  function requestClose() {
    if (dirty) setConfirmClose(true);
    else onClose();
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError(null);
    setSaved(false);
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        price: price === '' ? null : Number(price),
        description: description.trim() || undefined,
        imageMediaId: image?.id ?? null,
        isVisible,
        isFeatured,
      };
      if (isEditing) {
        await api.patch(`${base}/${item.id}`, payload);
        const normalizedDescription = payload.description ?? '';
        setName(payload.name);
        setDescription(normalizedDescription);
        initialValuesRef.current = JSON.stringify(currentValues({ name: payload.name, description: normalizedDescription }));
        setSaved(true);
        setDirty(false);
        onSaved();
      } else {
        await api.post(base, payload);
        onSaved();
        onClose();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal onClose={requestClose} className="item-editor" backdropClassName="item-editor-backdrop" labelledBy="item-editor-title" busy={submitting}>
      <header className="item-editor__header">
        <div>
          <span className="item-editor__eyebrow">{categoryName}</span>
          <h2 id="item-editor-title">{isEditing ? `Edit ${item.name}` : 'Add a menu item'}</h2>
          <p>{isEditing ? 'Update how this item appears to your guests.' : 'Fill in the details just as you would write them on a menu.'}</p>
        </div>
        <button type="button" className="item-editor__close" onClick={requestClose} disabled={submitting} aria-label="Close item editor"><Icon name="close" size={22} /></button>
      </header>

      <div className="item-editor__form">
        <div className="item-editor__body">
          {error && <p className="field-error">{error}</p>}
          {saved && <div className="alert alert-success item-editor__saved"><Icon name="check" size={16} /> Item details saved.</div>}

          <form id={detailsFormId} onSubmit={handleSubmit}>
            <section className="editor-form-section">
              <div className="editor-form-section__heading"><span className="editor-form-section__icon"><Icon name="plate" /></span><div><h3>Item details</h3><p>The information guests notice first.</p></div></div>
              <div className="editor-form-section__content">
                <div className="item-editor__field-grid">
                  <div className="field item-editor__name-field">
                    <label htmlFor="item-name">Item name <span aria-hidden="true">*</span></label>
                    <input id="item-name" type="text" required data-autofocus placeholder="e.g. Grilled chicken sandwich" value={name} onChange={(event) => change('name', setName, event.target.value)} />
                  </div>
                  <div className="field item-editor__price-field">
                    <label htmlFor="item-price">Price <span className="optional-label">Optional</span></label>
                    <div className="price-input"><span>{getCurrencySymbol(currency)}</span><input id="item-price" type="number" step="0.01" min="0" placeholder="0.00" value={price} onChange={(event) => change('price', setPrice, event.target.value)} /></div>
                  </div>
                </div>
                <div className="field">
                  <label htmlFor="item-description">Description <span className="optional-label">Optional</span></label>
                  <textarea id="item-description" rows="3" placeholder="What makes this dish special? Mention key ingredients or how it is served." value={description} onChange={(event) => change('description', setDescription, event.target.value)} />
                  <span className="field-hint">A short sentence is easiest for guests to scan.</span>
                </div>
              </div>
            </section>

            <section className="editor-form-section">
              <div className="editor-form-section__heading"><span className="editor-form-section__icon"><Icon name="image" /></span><div><h3>Dish photo</h3><p>Optional, but useful for signature dishes.</p></div></div>
              <div className="editor-form-section__content">
                <ImageUploadField value={image} onChange={(value) => change('imageMediaId', setImage, value)} hint="Use a clear square or landscape photo. Your image will be optimized automatically." />
              </div>
            </section>

            <section className="editor-form-section">
              <div className="editor-form-section__heading"><span className="editor-form-section__icon"><Icon name="sliders" /></span><div><h3>How it appears</h3><p>Control visibility and highlights.</p></div></div>
              <div className="editor-form-section__content editor-toggles">
                <label className="editor-toggle"><input type="checkbox" checked={isVisible} onChange={(event) => change('isVisible', setIsVisible, event.target.checked)} /><span className="editor-toggle__control" /><span><strong>Show on the menu</strong><small>Turn this off when an item is temporarily unavailable.</small></span></label>
                <label className="editor-toggle"><input type="checkbox" checked={isFeatured} onChange={(event) => change('isFeatured', setIsFeatured, event.target.checked)} /><span className="editor-toggle__control" /><span><strong>Mark as Popular</strong><small>Adds a small highlight to help this item stand out.</small></span></label>
              </div>
            </section>

          </form>

          {isEditing ? (
            <section className="editor-form-section editor-form-section--extras">
              <div className="editor-form-section__heading"><span className="editor-form-section__icon"><Icon name="info" /></span><div><h3>Details & customer choices</h3><p>Add dietary notes, sizes, extras, or other useful choices.</p></div></div>
              <div className="editor-form-section__content">
                <AttributesEditor menuId={menuId} categoryId={categoryId} itemId={item.id} attributes={item.attributes} onChanged={onSaved} />
                <OptionsEditor menuId={menuId} categoryId={categoryId} itemId={item.id} options={item.options} onChanged={onSaved} />
              </div>
            </section>
          ) : (
            <div className="item-editor__next-step"><Icon name="info" size={18} /><span><strong>Need sizes, add-ons, or dietary details?</strong> Save the item, then open it again to add customer choices such as “Small / Large” or labels such as “Vegan”.</span></div>
          )}
        </div>

        <footer className="item-editor__footer">
          <span>{isEditing ? (dirty ? 'You have unsaved item changes.' : 'Dietary details and choices save separately.') : 'You can add more details after saving.'}</span>
          <div><button type="button" className="btn" onClick={requestClose} disabled={submitting}>Cancel</button><button type="submit" form={detailsFormId} className="btn btn-primary" disabled={submitting || (isEditing && !dirty)} aria-busy={submitting || undefined}>{submitting ? 'Saving…' : isEditing ? 'Save item' : 'Add to menu'}</button></div>
        </footer>
      </div>
      {confirmClose && <ConfirmDialog
        title="Discard unsaved changes?"
        description="The item details you changed in this editor will be lost."
        confirmLabel="Discard changes"
        onConfirm={onClose}
        onCancel={() => setConfirmClose(false)}
      />}
    </Modal>
  );
}
